import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from "@angular/common/http";
import {Observable} from "rxjs";
import {AvailableSystem} from "../models/auth.models";
import {SecurityPolicy} from "../models/security-policy";
import {PsdRules} from "../models/psd-rules";
import {EmailConfirmationSettings} from "../models/email-confirmation-settings";

@Injectable({
  providedIn: 'root'
})
export class AuthFlowService {
  private readonly _httpClient = inject(HttpClient);

  public authenticate(body: { psd: string, xml: string }): Observable<AuthenticateResponse> {
    return this._httpClient.post<AuthenticateResponse>('/api/sso/SsoFlow', body);
  }

  public authenticateTest(body: { login: string, psd: string }): Observable<AuthenticateResponse> {
    return this._httpClient.post<AuthenticateResponse>('/api/sso/SsoFlow/auth-test', body);
  }

  public availableSystems(): Observable<AvailableSystem[]> {
    return this._httpClient.get<AvailableSystem[]>('/api/sso/SsoFlow/module/available');
  }

  public verifyEcpXml(body: { xml: string }): Observable<EcpSubject> {
    return this._httpClient.post<EcpSubject>('/api/sso/SsoFlow/ecp/verify-xml', body);
  }

  public registrationModules(): Observable<AvailableSystem[]> {
    return this._httpClient.get<AvailableSystem[]>('/api/sso/SsoFlow/module/register/modules');
  }

  public registrationProfiles(moduleId: string, xin: string): Observable<AvailableProfile[]> {
    return this._httpClient.get<AvailableProfile[]>(`/api/sso/SsoFlow/module/register/profiles?moduleId=${moduleId}&xin=${xin}`);
  }

  public registerModule(body: RegisterModuleRequest): Observable<RegisterModuleResponse> {
    return this._httpClient.post<RegisterModuleResponse>('/api/sso/SsoFlow/module/register', body);
  }

  public registerSsoUser(body: RegisterSsoUserRequest): Observable<RegisterModuleResponse> {
    return this._httpClient.post<RegisterModuleResponse>('/api/sso/SsoFlow/register', body);
  }

  public sendEmailCode(body: { email: string }): Observable<void> {
    return this._httpClient.post<void>('/api/sso/SsoFlow/email/send-code', body);
  }

  public confirmEmailCode(body: { email: string; code: string }): Observable<void> {
    return this._httpClient.post<void>('/api/sso/SsoFlow/email/confirm-code', body);
  }

  public emailConfirmationSettings(): Observable<EmailConfirmationSettings> {
    return this._httpClient.get<EmailConfirmationSettings>('/api/sso/SsoFlow/email/confirmation-settings');
  }

  public forgotPsd(body: { xml: string }): Observable<{ requiresEmailSetup: boolean }> {
    return this._httpClient.post<{ requiresEmailSetup: boolean }>('/api/sso/SsoFlow/psd/forgot', body);
  }

  public setEmailByXml(body: { xml: string; email: string }): Observable<void> {
    return this._httpClient.post<void>('/api/sso/SsoFlow/psd/set-email-by-xml', body);
  }

  public resetPsd(body: ResetPsdRequest): Observable<void> {
    return this._httpClient.post<void>('/api/sso/SsoFlow/psd/reset', body);
  }

  public consentDocument(): Observable<ConsentDocument> {
    return this._httpClient.get<ConsentDocument>('/api/sso/SsoFlow/consent/document');
  }

  public myConsent(): Observable<MyConsent | null> {
    return this._httpClient.get<MyConsent | null>('/api/sso/SsoFlow/consent/my');
  }

  public acceptConsent(body: { xml: string; consentToken?: string }): Observable<void> {
    return this._httpClient.post<void>('/api/sso/SsoFlow/consent/accept', body);
  }

  public changePsd(body: ChangePsdWithXmlRequest): Observable<ChangePsdWithXmlResponse> {
    return this._httpClient.post<ChangePsdWithXmlResponse>('/api/sso/SsoFlow/change-psd', body);
  }

  public managedUsers(filter: ManageUsersFilter): Observable<ManageUsersResponse> {
    let params = new HttpParams()
      .set('page', filter.page)
      .set('pageSize', filter.pageSize);

    if (filter.username) params = params.set('username', filter.username);
    if (filter.email) params = params.set('email', filter.email);

    return this._httpClient.get<ManageUsersResponse>(
      '/api/sso/ManageUsers/users/paging',
      {params}
    );
  }

  public blockManagedUser(userId: string, reason: string): Observable<void> {
    return this._httpClient.put<void>(
      `/api/sso/ManageUsers/users/${userId}/block`,
      {reason}
    );
  }

  public unblockManagedUser(userId: string): Observable<void> {
    return this._httpClient.put<void>(
      `/api/sso/ManageUsers/users/${userId}/unblock`,
      null
    );
  }

  public setManagedUserEmail(userId: string, email: string): Observable<void> {
    return this._httpClient.put<void>(
      `/api/sso/ManageUsers/users/${userId}/email`,
      {email}
    );
  }

  public setManagedUserRoles(userId: string, roles: string[]): Observable<void> {
    return this._httpClient.put<void>(
      `/api/sso/ManageUsers/users/${userId}/roles`,
      {roles}
    );
  }

  public setManagedUserPsd(userId: string, newPsd: string): Observable<void> {
    return this._httpClient.put<void>(
      `/api/sso/ManageUsers/users/${userId}/psd`,
      {newPsd}
    );
  }

  public psdRules(): Observable<PsdRules> {
    return this._httpClient.get<PsdRules>('/api/sso/SsoFlow/psd/rules');
  }

  public securityPolicy(): Observable<SecurityPolicy> {
    return this._httpClient.get<SecurityPolicy>('/api/sso/SecurityPolicy');
  }

  public updateSecurityPolicy(policy: SecurityPolicy): Observable<SecurityPolicy> {
    return this._httpClient.put<SecurityPolicy>('/api/sso/SecurityPolicy', policy);
  }

  public authLogs(filter: AuthLogsFilter, mine: boolean): Observable<AuthLogsResponse> {
    let params = new HttpParams()
      .set('page', filter.page)
      .set('pageSize', filter.pageSize);

    if (filter.login) params = params.set('login', filter.login);
    if (filter.eventType) params = params.set('eventType', filter.eventType);
    if (filter.from) params = params.set('from', filter.from);
    if (filter.to) params = params.set('to', filter.to);

    return this._httpClient.get<AuthLogsResponse>(
      mine ? '/api/sso/AuthLogs/my' : '/api/sso/AuthLogs',
      {params}
    );
  }
}

export interface AuthenticateResponse {
  success?: boolean;
  systems: AvailableSystem[];
  emailActionRequired?: boolean;
  emailConfirmationRequired?: boolean;
  email?: string;
  psdExpired?: boolean;
  psdResetToken?: string;
  psdTemporary?: boolean;
  consentRequired?: boolean;
  consentToken?: string;
}

export interface ConsentDocument {
  version: string;
  titleKz: string;
  titleRu: string;
  textKz: string;
  textRu: string;
  xml: string;
  documentUrl: string;
}

export interface MyConsent {
  version: string;
  signedBy?: string | null;
  signedAt: string;
  documentUrl: string;
}

export interface AvailableProfile {
  roleId: number;
  roleName?: string;
  orgName?: string;
}

export interface EcpSubject {
  iin: string
  bin: string
  firstName: string
  lastName: string
  middleName: string
  organizationName: string
}

export interface RegisterModuleRequest {
  xml: string;
  xin: string;
  password: string;
  confirmPassword: string;
  moduleId: string;
  name: string;
  surname: string;
  patronymic?: string;
  email: string;
  phoneNumber: string;
  organizationType?: string;
  organizationId?: string;
  organizationName?: string;
  roleId?: number;
  roleName?: string;
}

export interface RegisterSsoUserRequest {
  xml: string;
  iin: string;
  psd: string;
  confirmPsd: string;
  email: string;
  emailCode: string;
}

export interface ResetPsdRequest {
  token: string;
  newPsd: string;
  confirmPsd: string;
}

export interface ChangePsdWithXmlRequest {
  xml: string;
  newPsd: string;
  confirmPsd: string;
}

export interface ChangePsdWithXmlResponse {
  id: string;
  xin: string;
}

export interface RegisterModuleResponse {
  id: string;
  xin: string;
  email?: string;
  isBlocked?: boolean;
}

export interface ManageUsersFilter {
  page: number;
  pageSize: number;
  username?: string;
  email?: string;
}

export interface ManageUser {
  id: string;
  email: string;
  username: string;
  lastActivityDate?: string | null;
  lockedReason?: string | null;
  accountState?: number | 'Active' | 'Locked' | null;
  roles: string[];
}

export interface ManageUsersResponse {
  list: ManageUser[];
  totalElements: number;
  totalPage: number;
}

export const AUTH_CLIENT_UNKNOWN = 0;
export const AUTH_CLIENT_WEB = 1;
export const AUTH_CLIENT_ISZH_MOBILE = 2;
export const AUTH_CLIENT_TORT_TULIK = 3;
export const AUTH_CLIENT_LEGACY_MOBILE = 4;

export const AUTH_EVENT_LOGIN_SUCCESS = 1;
export const AUTH_EVENT_LOGIN_FAILED = 2;
export const AUTH_EVENT_LOGOUT = 3;

export interface AuthLogsFilter {
  page: number;
  pageSize: number;
  login?: string;
  eventType?: number | null;
  from?: string;
  to?: string;
}

export interface AuthLogRow {
  id: number;
  userId?: string | null;
  login?: string | null;
  eventType: number;
  reason?: string | null;
  client: number;
  ipAddress?: string | null;
  userAgent?: string | null;
  createdAt: string;
}

export interface AuthLogsResponse {
  list: AuthLogRow[];
  totalElements: number;
  totalPage: number;
}
